import type {
  HoldingUnitAdjustmentType,
  HoldingUnitIdentifier,
  HoldingUnitLifecycleStatus,
  HoldingUnitLoadSessionItemSelectionMethod,
  HoldingUnitLoadSessionStatus,
  HoldingUnitProfile,
  HoldingUnitProfileSnapshotMetadata,
  HoldingUnitReservationTargetType,
  HoldingUnitWeightInputMode,
} from './holding-units.dto.js';
import type { HoldingUnitEventEnvelopeBase } from './holding-unit-event-envelope.dto.js';

export const holdingUnitEventTypeValues = [
  'holdingUnitCreated',
  'holdingUnitMaterialSet',
  'holdingUnitMaterialCorrected',
  'holdingUnitContainerIdentityCorrected',
  'holdingUnitIdentifierAssigned',
  'holdingUnitYardLocationMoved',
  'holdingUnitTareUpdated',
  'holdingUnitWeightAdded',
  'holdingUnitWeightCorrected',
  'holdingUnitAdjustmentApplied',
  'holdingUnitTransferCommitted',
  'holdingUnitBuildBaleCommitted',
  'holdingUnitInventoryImpactReversed',
  'holdingUnitStatusChanged',
  'holdingUnitReservationCreated',
  'holdingUnitReservationReleased',
  'holdingUnitLoadSessionCreated',
  'holdingUnitLoadSessionRenamed',
  'holdingUnitLoadSessionStatusChanged',
  'holdingUnitLoadSessionMaterialGroupCreated',
  'holdingUnitLoadSessionMaterialGroupUpdated',
  'holdingUnitLoadSessionItemAdded',
  'holdingUnitLoadSessionItemRemoved',
  'holdingUnitLoadSessionItemMeasuredDiscrepancyCommitted',
  'holdingUnitLoadSessionItemMeasuredDiscrepancyDiscarded',
] as const;
export type HoldingUnitEventType = (typeof holdingUnitEventTypeValues)[number];

export interface HoldingUnitCreatedEventPayload
  extends HoldingUnitProfile,
    HoldingUnitProfileSnapshotMetadata {
  lifecycleStatus: HoldingUnitLifecycleStatus;
  fixedIdAssetId?: string | null;
}

export interface HoldingUnitMaterialSetEventPayload {
  materialId: string;
}

export interface HoldingUnitAuditWeightBreakdown {
  grossLb: number | null;
  tareLb: number | null;
  netLb: number;
}

export interface HoldingUnitAuditWeightDelta {
  grossLb: number | null;
  tareLb: number | null;
  netLb: number;
}

export interface HoldingUnitReasonAuditPayload {
  reason?: string | null;
  reasonRequired?: boolean;
  withinGracePeriod?: boolean;
  createdAt?: string;
  auditFlags?: string[];
}

export interface HoldingUnitMaterialCorrectedEventPayload {
  previousMaterialId: string;
  materialId: string;
  previousWeight?: HoldingUnitAuditWeightBreakdown;
  newWeight?: HoldingUnitAuditWeightBreakdown;
  deltaWeight?: HoldingUnitAuditWeightDelta;
  reason?: string | null;
  reasonRequired?: boolean;
  withinGracePeriod?: boolean;
  createdAt?: string;
  auditFlags?: string[];
}

export interface HoldingUnitContainerIdentityCorrectedEventPayload {
  previousAssetId: string;
  correctedAssetId: string;
  previousBusinessIdentifier: string;
  correctedBusinessIdentifier: string;
  previousNormalizedPhysicalId: string;
  correctedNormalizedPhysicalId: string;
  previousNormalizedScannableIdentifier: string;
  correctedNormalizedScannableIdentifier: string;
  unchangedGrossLb: number | null;
  unchangedTareLb: number | null;
  unchangedNetLb: number;
  unchangedLifecycleStatus: Extract<HoldingUnitLifecycleStatus, 'open' | 'ready'>;
}

export interface HoldingUnitIdentifierAssignedEventPayload {
  identifier: HoldingUnitIdentifier;
  normalizedPhysicalId?: string | null;
  normalizedScannableIdentifier?: string | null;
}

export interface HoldingUnitYardLocationMovedEventPayload {
  yardLocationId: string | null;
}

export interface HoldingUnitTareUpdatedEventPayload {
  tareLb?: number;
  tareSuggestedLb?: number | null;
}

export interface HoldingUnitWeightAddedEventPayload {
  inputMode: HoldingUnitWeightInputMode;
  grossTotalLb: number | null;
  tareLb: number | null;
  netLb: number;
  deltaNetLb: number;
  isManualNetDelta: boolean;
}

export interface HoldingUnitWeightCorrectedEventPayload {
  auditBasisEventId?: string | null;
  inputMode: HoldingUnitWeightInputMode;
  grossTotalLb: number | null;
  tareLb: number | null;
  netLb: number;
  deltaNetLb: number;
  previousWeight?: HoldingUnitAuditWeightBreakdown;
  newWeight?: HoldingUnitAuditWeightBreakdown;
  deltaWeight?: HoldingUnitAuditWeightDelta;
  reason?: string | null;
  reasonRequired?: boolean;
  withinGracePeriod?: boolean;
  createdAt?: string;
  auditFlags?: string[];
}

export interface HoldingUnitAdjustmentAppliedEventPayload {
  adjustmentType: HoldingUnitAdjustmentType;
  deltaNetLb: number;
  reason: string;
}

export interface HoldingUnitTransferCommittedEventPayload {
  fromUnitId: string;
  toUnitId: string;
  netLb: number;
  batchId?: string;
  mode?: 'bulk_all_net';
  lineId?: string;
  lineIndex?: number;
  sourceUnitCount?: number;
  batchTotalNetLb?: number;
  materialId?: string;
  destinationMaterialAdopted?: boolean;
}

export interface HoldingUnitBuildBaleCommittedEventPayload {
  schemaVersion: 1;
  batchId: string;
  stagingUnitId: string;
  sourceUnitIds: string[];
  productUnitIds: string[];
  leftoverUnitId?: string | null;
  leftoverTargetUnitId?: string | null;
  materialId: string;
  sourceNetLb: number;
  productNetLb: number;
  leftoverNetLb: number;
  remainingStagingNetLb: number;
}

export interface HoldingUnitInventoryImpactReversedEventPayload {
  reason?: string | null;
  reasonRequired: boolean;
  withinGracePeriod: boolean;
  createdAt: string;
  removedAt: string;
  fromLifecycleStatus: Extract<HoldingUnitLifecycleStatus, 'open' | 'ready'>;
  toLifecycleStatus: 'retired';
  removedWeight: HoldingUnitAuditWeightBreakdown;
  reversalDelta: HoldingUnitAuditWeightDelta;
  auditFlags: string[];
}

export interface HoldingUnitLifecycleStatusChangedEventPayload {
  fromLifecycleStatus: HoldingUnitLifecycleStatus;
  toLifecycleStatus: HoldingUnitLifecycleStatus;
  reason?: string | null;
  schemaVersion?: 2;
  shipmentId?: string | null;
  coverShipmentId?: string | null;
  shippedNetLb?: number | null;
  materialId?: string | null;
  isCrvEligible?: boolean | null;
}

export interface HoldingUnitReservationCreatedEventPayload {
  reservationId: string;
  reservationType: HoldingUnitReservationTargetType;
  loadSessionId?: string | null;
  shipmentId?: string | null;
}

export interface HoldingUnitReservationReleasedEventPayload {
  reservationId: string;
}

export interface HoldingUnitLoadSessionCreatedEventPayload {
  loadSessionId: string;
  status: HoldingUnitLoadSessionStatus;
  sessionName?: string | null;
  toleranceProfileKey?: string | null;
}

export interface HoldingUnitLoadSessionRenamedEventPayload {
  loadSessionId: string;
  sessionName: string | null;
}

export interface HoldingUnitLoadSessionStatusChangedEventPayload {
  loadSessionId: string;
  fromStatus: HoldingUnitLoadSessionStatus;
  toStatus: HoldingUnitLoadSessionStatus;
}

export interface HoldingUnitLoadSessionMaterialGroupCreatedEventPayload {
  loadSessionId: string;
  groupId: string;
  materialId: string;
  autoPickEnabled: boolean;
  toleranceProfileKey: string | null;
  yardLocationIds: string[] | null;
}

export interface HoldingUnitLoadSessionMaterialGroupUpdatedEventPayload {
  loadSessionId: string;
  groupId: string;
  autoPickEnabled: boolean;
  toleranceProfileKey: string | null;
  yardLocationIds: string[] | null;
}

export interface HoldingUnitLoadSessionItemAddedEventPayload {
  loadSessionId: string;
  groupId: string;
  itemId: string;
  unitId: string;
  selectionMethod: HoldingUnitLoadSessionItemSelectionMethod;
  snapshotNetLbAtSelection: number;
  snapshotGrossLbAtSelection: number | null;
  snapshotTareLbAtSelection: number | null;
  loadMeasuredGrossLb: number | null;
  loadMeasuredNetLb: number | null;
  deltaLossLb: number | null;
  toleranceProfileKey: string | null;
  wasAutoPicked: boolean;
}

export interface HoldingUnitLoadSessionItemRemovedEventPayload {
  loadSessionId: string;
  groupId: string;
  itemId: string;
}

export interface HoldingUnitLoadSessionItemMeasuredDiscrepancyCommittedEventPayload {
  loadSessionId: string;
  groupId: string;
  itemId: string;
  unitId: string;
  committedWeightEventId: string;
  committedWeightEventType: 'holdingUnitWeightCorrected';
}

export interface HoldingUnitLoadSessionItemMeasuredDiscrepancyDiscardedEventPayload {
  loadSessionId: string;
  groupId: string;
  itemId: string;
  unitId: string;
}

export interface HoldingUnitEventBase
  extends Omit<HoldingUnitEventEnvelopeBase, 'entityType' | 'eventType' | 'payload'> {
  entityType: 'holdingUnit' | 'loadSession';
  eventType: HoldingUnitEventType;
}

export interface HoldingUnitCreatedEvent extends HoldingUnitEventBase {
  eventType: 'holdingUnitCreated';
  payload: HoldingUnitCreatedEventPayload;
}

export interface HoldingUnitMaterialSetEvent extends HoldingUnitEventBase {
  eventType: 'holdingUnitMaterialSet';
  payload: HoldingUnitMaterialSetEventPayload;
}

export interface HoldingUnitMaterialCorrectedEvent extends HoldingUnitEventBase {
  eventType: 'holdingUnitMaterialCorrected';
  payload: HoldingUnitMaterialCorrectedEventPayload;
}

export interface HoldingUnitContainerIdentityCorrectedEvent extends HoldingUnitEventBase {
  eventType: 'holdingUnitContainerIdentityCorrected';
  payload: HoldingUnitContainerIdentityCorrectedEventPayload;
}

export interface HoldingUnitIdentifierAssignedEvent extends HoldingUnitEventBase {
  eventType: 'holdingUnitIdentifierAssigned';
  payload: HoldingUnitIdentifierAssignedEventPayload;
}

export interface HoldingUnitYardLocationMovedEvent extends HoldingUnitEventBase {
  eventType: 'holdingUnitYardLocationMoved';
  payload: HoldingUnitYardLocationMovedEventPayload;
}

export interface HoldingUnitTareUpdatedEvent extends HoldingUnitEventBase {
  eventType: 'holdingUnitTareUpdated';
  payload: HoldingUnitTareUpdatedEventPayload;
}

export interface HoldingUnitWeightAddedEvent extends HoldingUnitEventBase {
  eventType: 'holdingUnitWeightAdded';
  payload: HoldingUnitWeightAddedEventPayload;
}

export interface HoldingUnitWeightCorrectedEvent extends HoldingUnitEventBase {
  eventType: 'holdingUnitWeightCorrected';
  payload: HoldingUnitWeightCorrectedEventPayload;
}

export interface HoldingUnitAdjustmentAppliedEvent extends HoldingUnitEventBase {
  eventType: 'holdingUnitAdjustmentApplied';
  payload: HoldingUnitAdjustmentAppliedEventPayload;
}

export interface HoldingUnitTransferCommittedEvent extends HoldingUnitEventBase {
  eventType: 'holdingUnitTransferCommitted';
  payload: HoldingUnitTransferCommittedEventPayload;
}

export interface HoldingUnitBuildBaleCommittedEvent extends HoldingUnitEventBase {
  eventType: 'holdingUnitBuildBaleCommitted';
  payload: HoldingUnitBuildBaleCommittedEventPayload;
}

export interface HoldingUnitInventoryImpactReversedEvent extends HoldingUnitEventBase {
  eventType: 'holdingUnitInventoryImpactReversed';
  payload: HoldingUnitInventoryImpactReversedEventPayload;
}

export interface HoldingUnitLifecycleStatusChangedEvent extends HoldingUnitEventBase {
  eventType: 'holdingUnitStatusChanged';
  payload: HoldingUnitLifecycleStatusChangedEventPayload;
}

export interface HoldingUnitReservationCreatedEvent extends HoldingUnitEventBase {
  eventType: 'holdingUnitReservationCreated';
  payload: HoldingUnitReservationCreatedEventPayload;
}

export interface HoldingUnitReservationReleasedEvent extends HoldingUnitEventBase {
  eventType: 'holdingUnitReservationReleased';
  payload: HoldingUnitReservationReleasedEventPayload;
}

export interface HoldingUnitLoadSessionCreatedEvent extends HoldingUnitEventBase {
  eventType: 'holdingUnitLoadSessionCreated';
  payload: HoldingUnitLoadSessionCreatedEventPayload;
}

export interface HoldingUnitLoadSessionRenamedEvent extends HoldingUnitEventBase {
  eventType: 'holdingUnitLoadSessionRenamed';
  payload: HoldingUnitLoadSessionRenamedEventPayload;
}

export interface HoldingUnitLoadSessionStatusChangedEvent extends HoldingUnitEventBase {
  eventType: 'holdingUnitLoadSessionStatusChanged';
  payload: HoldingUnitLoadSessionStatusChangedEventPayload;
}

export interface HoldingUnitLoadSessionMaterialGroupCreatedEvent extends HoldingUnitEventBase {
  eventType: 'holdingUnitLoadSessionMaterialGroupCreated';
  payload: HoldingUnitLoadSessionMaterialGroupCreatedEventPayload;
}

export interface HoldingUnitLoadSessionMaterialGroupUpdatedEvent extends HoldingUnitEventBase {
  eventType: 'holdingUnitLoadSessionMaterialGroupUpdated';
  payload: HoldingUnitLoadSessionMaterialGroupUpdatedEventPayload;
}

export interface HoldingUnitLoadSessionItemAddedEvent extends HoldingUnitEventBase {
  eventType: 'holdingUnitLoadSessionItemAdded';
  payload: HoldingUnitLoadSessionItemAddedEventPayload;
}

export interface HoldingUnitLoadSessionItemRemovedEvent extends HoldingUnitEventBase {
  eventType: 'holdingUnitLoadSessionItemRemoved';
  payload: HoldingUnitLoadSessionItemRemovedEventPayload;
}

export interface HoldingUnitLoadSessionItemMeasuredDiscrepancyCommittedEvent
  extends HoldingUnitEventBase {
  eventType: 'holdingUnitLoadSessionItemMeasuredDiscrepancyCommitted';
  payload: HoldingUnitLoadSessionItemMeasuredDiscrepancyCommittedEventPayload;
}

export interface HoldingUnitLoadSessionItemMeasuredDiscrepancyDiscardedEvent
  extends HoldingUnitEventBase {
  eventType: 'holdingUnitLoadSessionItemMeasuredDiscrepancyDiscarded';
  payload: HoldingUnitLoadSessionItemMeasuredDiscrepancyDiscardedEventPayload;
}

type HoldingUnitOrLoadSessionEvent =
  | HoldingUnitCreatedEvent
  | HoldingUnitMaterialSetEvent
  | HoldingUnitMaterialCorrectedEvent
  | HoldingUnitContainerIdentityCorrectedEvent
  | HoldingUnitIdentifierAssignedEvent
  | HoldingUnitYardLocationMovedEvent
  | HoldingUnitTareUpdatedEvent
  | HoldingUnitWeightAddedEvent
  | HoldingUnitWeightCorrectedEvent
  | HoldingUnitAdjustmentAppliedEvent
  | HoldingUnitTransferCommittedEvent
  | HoldingUnitBuildBaleCommittedEvent
  | HoldingUnitInventoryImpactReversedEvent
  | HoldingUnitLifecycleStatusChangedEvent
  | HoldingUnitReservationCreatedEvent
  | HoldingUnitReservationReleasedEvent
  | HoldingUnitLoadSessionCreatedEvent
  | HoldingUnitLoadSessionRenamedEvent
  | HoldingUnitLoadSessionStatusChangedEvent
  | HoldingUnitLoadSessionMaterialGroupCreatedEvent
  | HoldingUnitLoadSessionMaterialGroupUpdatedEvent
  | HoldingUnitLoadSessionItemAddedEvent
  | HoldingUnitLoadSessionItemRemovedEvent
  | HoldingUnitLoadSessionItemMeasuredDiscrepancyCommittedEvent
  | HoldingUnitLoadSessionItemMeasuredDiscrepancyDiscardedEvent;

type LoadSessionEventVariant =
  | HoldingUnitLoadSessionCreatedEvent
  | HoldingUnitLoadSessionRenamedEvent
  | HoldingUnitLoadSessionStatusChangedEvent
  | HoldingUnitLoadSessionMaterialGroupCreatedEvent
  | HoldingUnitLoadSessionMaterialGroupUpdatedEvent
  | HoldingUnitLoadSessionItemAddedEvent
  | HoldingUnitLoadSessionItemRemovedEvent
  | HoldingUnitLoadSessionItemMeasuredDiscrepancyCommittedEvent
  | HoldingUnitLoadSessionItemMeasuredDiscrepancyDiscardedEvent;

export type LoadSessionStreamEvent = LoadSessionEventVariant & { entityType: 'loadSession' };

export type HoldingUnitStreamEvent = Exclude<
  HoldingUnitOrLoadSessionEvent,
  LoadSessionEventVariant
> & { entityType: 'holdingUnit' };

export type HoldingUnitAndLoadSessionEvent = HoldingUnitStreamEvent | LoadSessionStreamEvent;
